import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MathRenderer from '../components/MathRenderer';
import { SkeletonQuestion } from '../components/Skeletons';
import { toast } from 'sonner';
import { Lightbulb, SkipForward, ArrowRight, Coins } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';

export default function DailyPractice() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [result, setResult] = useState(null);
  const [showHintText, setShowHintText] = useState(false);
  const [skipsLeft, setSkipsLeft] = useState(2);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [sessionStartTime] = useState(Date.now());
  const [coinsEarned, setCoinsEarned] = useState(0);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await axios.get(`${API}/student/session/start`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true
        });
        setSession(res.data.session);
        setQuestions(res.data.questions);
        const answeredCount = (res.data.answers || []).length;
        setCurrentIdx(answeredCount);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error(err.response.data.detail);
          navigate('/home', { replace: true });
        } else {
          toast.error('Could not start session');
          navigate('/home', { replace: true });
        }
      } finally { setLoading(false); }
    };
    fetchSession();
  }, [API, token, navigate]);

  const submitAnswer = useCallback(async (option) => {
    if (submitting || result) return;
    setSelectedOption(option);
    setSubmitting(true);
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    try {
      const res = await axios.post(`${API}/student/answer`, {
        session_id: session.id, question_id: questions[currentIdx].id,
        selected_option: option, time_taken_seconds: timeTaken, hint_used: showHintText
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setResult(res.data);
      if (res.data.is_correct) {
        setCoinsEarned(prev => prev + (res.data.coins_earned || 10));
        confetti({ particleCount: 60, spread: 60, origin: { y: 0.7 }, colors: ['#FF6B35', '#FFD23F', '#06D6A0'] });
      }
    } catch { toast.error('Failed to submit answer'); }
    finally { setSubmitting(false); }
  }, [submitting, result, startTime, API, session, questions, currentIdx, showHintText, token]);

  const nextQuestion = () => {
    if (currentIdx + 1 >= questions.length) {
      completeSession();
      return;
    }
    setCurrentIdx(p => p + 1);
    setSelectedOption(null);
    setResult(null);
    setShowHintText(false);
    setStartTime(Date.now());
  };

  const skipQuestion = () => {
    if (skipsLeft <= 0) { toast.error('No skips left!'); return; }
    setSkipsLeft(p => p - 1);
    nextQuestion();
  };

  const completeSession = async () => {
    const totalTime = Math.round((Date.now() - sessionStartTime) / 1000);
    try {
      const res = await axios.post(`${API}/student/session/complete`, {
        session_id: session.id, time_taken_seconds: totalTime
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      navigate('/session-complete', { state: { result: res.data, sessionId: session.id }, replace: true });
    } catch { toast.error('Failed to complete session'); }
  };

  if (loading) return (
    <div className="min-h-screen pt-4" style={{ background: '#FFF8F0' }}>
      <SkeletonQuestion />
    </div>
  );

  if (!questions.length) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FFF8F0' }}>
      <div className="text-center">
        <span className="text-5xl block mb-4">📚</span>
        <p className="text-lg font-bold" style={{ color: '#1B4F8A' }}>No questions available for your class yet!</p>
        <button onClick={() => navigate('/home')} className="btn-press mt-4 px-6 py-3 rounded-full font-bold text-white" style={{ background: '#FF6B35' }}>Go Home</button>
      </div>
    </div>
  );

  const q = questions[currentIdx];
  if (!q) { completeSession(); return null; }

  const options = [
    { key: 'A', text: q.option_a },
    { key: 'B', text: q.option_b },
    { key: 'C', text: q.option_c },
    { key: 'D', text: q.option_d },
  ];

  const diffColors = { foundation: '#06D6A0', exam_ready: '#FFD23F', champion: '#FF6B35' };
  const diffLabels = { foundation: 'Foundation', exam_ready: 'Exam Ready', champion: 'Champion' };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#FFF8F0' }}>
      {/* Progress Bar */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>Question {currentIdx + 1} of {questions.length}</p>
          <div className="flex items-center gap-3">
            {coinsEarned > 0 && (
              <span className="flex items-center gap-1 text-xs font-bold" style={{ color: '#FFD23F' }}>
                <Coins size={14} /> +{coinsEarned}
              </span>
            )}
            {skipsLeft > 0 && !result && (
              <button data-testid="skip-btn" onClick={skipQuestion} className="text-xs font-bold flex items-center gap-1" style={{ color: '#8B7355' }}>
                <SkipForward size={14} /> Skip ({skipsLeft})
              </button>
            )}
          </div>
        </div>
        <div className="w-full h-3 rounded-full" style={{ background: '#E5DACE' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((currentIdx + 1) / questions.length) * 100}%`, background: '#FF6B35' }} />
        </div>
      </div>

      {/* Chapter & Difficulty */}
      <div className="px-4 flex items-center gap-2 mb-4">
        <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: diffColors[q.difficulty] + '20', color: diffColors[q.difficulty] }}>
          {diffLabels[q.difficulty] || q.difficulty}
        </span>
        <span className="text-xs font-semibold" style={{ color: '#8B7355' }}>{q.chapter}</span>
      </div>

      {/* Question */}
      <div className="px-4 mb-6 flex-1">
        <div className="bg-white border-2 rounded-3xl p-5" style={{ borderColor: '#E5DACE', boxShadow: '0 4px 0 #E5DACE' }} data-testid="question-card">
          <p className="text-lg font-extrabold leading-relaxed" style={{ color: '#1B4F8A' }}>
            <MathRenderer text={q.question_latex || q.question_text} />
          </p>
        </div>

        {/* Hint */}
        {showHintText && q.hint && !result && (
          <div className="mt-3 bg-[#FFD23F]/15 border-2 border-[#FFD23F] rounded-2xl p-3 animate-slide-up">
            <p className="text-xs font-bold" style={{ color: '#1B4F8A' }}>Hint: <MathRenderer text={q.hint} /></p>
          </div>
        )}

        {/* Options */}
        <div className="mt-4 space-y-3" data-testid="options-container">
          {options.map(opt => {
            let optStyle = { borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE', color: '#1B4F8A', background: 'white' };
            let extraClass = '';
            if (result) {
              if (opt.key === result.correct_option.toUpperCase()) {
                optStyle = { background: '#06D6A0', borderColor: '#04A77C', boxShadow: '0 3px 0 #04A77C', color: 'white' };
              } else if (opt.key === selectedOption?.toUpperCase() && !result.is_correct) {
                optStyle = { background: '#EF4444', borderColor: '#DC2626', boxShadow: '0 3px 0 #DC2626', color: 'white' };
                extraClass = 'animate-shake';
              }
            }
            return (
              <button key={opt.key} data-testid={`option-${opt.key}`}
                onClick={() => submitAnswer(opt.key)} disabled={!!result}
                className={`btn-press w-full rounded-2xl p-4 flex items-center gap-3 text-left font-bold text-sm transition-all border-2 ${extraClass}`}
                style={optStyle}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                  style={{
                    background: result && opt.key === result.correct_option.toUpperCase() ? 'rgba(255,255,255,0.3)' : '#F0E6D8',
                    color: result && opt.key === result.correct_option.toUpperCase() ? 'white' : '#1B4F8A'
                  }}>
                  {result ? (opt.key === result.correct_option.toUpperCase() ? '✓' : (opt.key === selectedOption?.toUpperCase() && !result.is_correct ? '✗' : opt.key)) : opt.key}
                </span>
                <MathRenderer text={opt.text} />
              </button>
            );
          })}
        </div>

        {/* Solution Panel */}
        {result && (
          <div className="mt-4 animate-slide-up space-y-3">
            {(result.solution || result.solution_latex) && (
              <div className="bg-white border-2 rounded-2xl p-4" style={{ borderColor: '#E5DACE' }}>
                <p className="text-xs font-bold mb-1" style={{ color: '#06D6A0' }}>Solution</p>
                <p className="text-sm font-semibold" style={{ color: '#1B4F8A' }}>
                  <MathRenderer text={result.solution_latex || result.solution} />
                </p>
              </div>
            )}
            {result.common_mistake && (
              <div className="bg-[#FFD23F]/10 border-2 border-[#FFD23F] rounded-2xl p-3">
                <p className="text-xs font-bold" style={{ color: '#FF6B35' }}>Common Mistake: {result.common_mistake}</p>
              </div>
            )}
            {result.is_correct && (
              <div className="flex items-center justify-center gap-1 text-sm font-bold" style={{ color: '#FFD23F' }}>
                <Coins size={16} /> +10 MathCoins!
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="px-4 pb-6 flex gap-3">
        {!result && (
          <button data-testid="hint-btn" onClick={() => setShowHintText(true)} disabled={showHintText}
            className="btn-press flex-1 py-3 rounded-full font-bold text-sm border-2" style={{ borderColor: '#FFD23F', color: '#1B4F8A', opacity: showHintText ? 0.5 : 1 }}>
            <Lightbulb size={16} className="inline mr-1" /> Hint
          </button>
        )}
        {result && (
          <button data-testid="next-question-btn" onClick={nextQuestion}
            className="btn-press flex-1 py-4 rounded-full font-extrabold text-white text-base" style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918' }}>
            {currentIdx + 1 >= questions.length ? 'See Results' : 'Next Question'} <ArrowRight size={18} className="inline ml-1" />
          </button>
        )}
      </div>
    </div>
  );
}
